"use client";

import { useRouter, usePathname, useSearchParams } from "next/navigation";
import React from "react";

import { useTranslations } from "next-intl";

import { cn } from "@/utils/cn";
import { PlansFilterParams } from "@/utils/filterLayouts";

import { RoomButton } from "./RoomButton";
import styles from "./style.module.scss";

interface FilterRoomsProps {
  currentFilters: PlansFilterParams;
  className?: string;
}

const RoomsDisplayOptions = ["1", "2", "3", "4", "5+"];

export const FilterRooms: React.FC<FilterRoomsProps> = ({
  currentFilters,
  className,
}) => {
  const t = useTranslations();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const updateFilters = (newRooms: number[]) => {
    const params = new URLSearchParams(searchParams.toString());

    // Remove all existing rooms[] parameters
    const allParams = Array.from(params.keys());
    allParams.forEach((key) => {
      if (key === "rooms[]") {
        params.delete(key);
      }
    });

    // Add new rooms[] parameters
    newRooms.forEach((room) => {
      params.append("rooms[]", room.toString());
    });

    router.push(`${pathname}?${params.toString()}`);
  };

  const handleRoomToggle = (displayValue: string) => {
    const currentRooms = currentFilters.rooms || [];

    if (displayValue === "5+") {
      // For 5+ handle rooms 5,6,7,8,9,10
      const fivePlusRooms = [5, 6, 7, 8, 9, 10];
      const hasFivePlus = fivePlusRooms.some((room) =>
        currentRooms.includes(room),
      );

      if (hasFivePlus) {
        // Remove all 5+ rooms
        const newRooms = currentRooms.filter(
          (room) => !fivePlusRooms.includes(room),
        );
        updateFilters(newRooms);
      } else {
        // Add all 5+ rooms
        const newRooms = [...currentRooms];
        fivePlusRooms.forEach((room) => {
          if (!newRooms.includes(room)) {
            newRooms.push(room);
          }
        });
        updateFilters(newRooms);
      }
    } else {
      // For 1, 2, 3, 4
      const room = parseInt(displayValue);
      if (currentRooms.includes(room)) {
        const newRooms = currentRooms.filter((r) => r !== room);
        updateFilters(newRooms);
      } else {
        const newRooms = [...currentRooms, room];
        updateFilters(newRooms);
      }
    }
  };

  const isRoomSelected = (displayValue: string): boolean => {
    const currentRooms = currentFilters.rooms || [];

    if (displayValue === "5+") {
      const fivePlusRooms = [5, 6, 7, 8, 9, 10];
      return fivePlusRooms.some((room) => currentRooms.includes(room));
    } else {
      const room = parseInt(displayValue);
      return currentRooms.includes(room);
    }
  };

  return (
    <div className={cn(styles.filterRooms, className)}>
      <div className={styles.label}>{t("Rooms")}</div>
      <div className={styles.buttonsWrapper}>
        {RoomsDisplayOptions.map((displayValue) => (
          <RoomButton
            key={displayValue}
            variant={isRoomSelected(displayValue) ? "active" : "inactive"}
            onClick={() => handleRoomToggle(displayValue)}
          >
            {displayValue}
          </RoomButton>
        ))}
      </div>
    </div>
  );
};
